import axios from "axios";
import _ from "lodash";
import moment from "moment";
import { DATE_FORMAT_DD_MM_YYYY, DATE_FORMAT_YYYY_MM_DD } from "../../constants/DateConstant";
import { getFirebaseIDTokenForShop } from "../firebase/firebase";
import { resolveShopApiUrl } from "./shopApi";

const TRUSTPILOT_REVIEW_PATH = "/api/marketing/trustpilot/review";

/**
 * Which orders get a Trustpilot review invitation, and what the api is told about them.
 *
 * Two filters, both of them deliberate and both older than this module:
 *  - **gmail addresses are dropped.** Those are invited through a separate flow.
 *  - **orders already carrying `trustpilotReviewInvitationSent` are dropped**, so a second
 *    export run on the same day does not mail the same customer twice.
 *
 * On top of that the list is deduplicated **by e-mail**, not by order: a customer with two
 * orders on one delivery day gets one invitation, not two.
 *
 * Orders without an e-mail address are skipped instead of crashing the export - `String.includes`
 * on an undefined address used to take the whole export down with it.
 */
export const buildTrustpilotInvitationList = (orders) =>
  _.uniqBy(
    (orders || [])
      .map((order) => ({
        email: order?.customer?.email || "",
        name: `${order?.customer?.firstName || ""} ${order?.customer?.lastName || ""}`.trim(),
        // moment, not dayjs: `dayjs(date, "DD-MM-YYYY")` silently ignores the format string
        // unless the customParseFormat plugin is loaded, which it is not in this app. That is
        // why this payload used to carry the literal string "Invalid Date" into the invitation
        // mail's Freemarker model (api/, MarketingService#421). moment parses the format natively.
        deliveryDate: moment(order?.delivery?.date, DATE_FORMAT_DD_MM_YYYY).format(DATE_FORMAT_YYYY_MM_DD),
        orderDate: moment.unix(order?.createdAt).format(DATE_FORMAT_YYYY_MM_DD),
        orderNumber: order?.orderId,
        trustpilotReviewInvitationSent: order?.trustpilotReviewInvitationSent,
      }))
      .filter((object) => !!object.email && !object.email.includes("gmail") && !object.trustpilotReviewInvitationSent),
    "email",
  );

/**
 * Send the Trustpilot review invitations for a set of orders that have just been handed to a
 * carrier, and mark those orders as invited.
 *
 * This is the ONLY Trustpilot invitation in the CMS. It used to live inline in
 * `handleOptimoRouteExport`, which meant the invitations stopped the moment regional orders
 * moved from OptimoRoute to SooCool. Both export paths now call this one function, so they
 * cannot drift apart.
 *
 * Per shop, because VCE and HVO each run their own api deploy against their own Firebase
 * project: a VCE-issued ID token is rejected by the HVO api, and the VCE api has never heard of
 * an HVO order. `resolveShopApiUrl` / `getFirebaseIDTokenForShop` do the same split that
 * `createSooCoolShipments` already does. For a VCE-only set (everything OptimoRoute ever saw)
 * this resolves to exactly the old host and the old token.
 *
 * Timing is unchanged: invitations go out at export time, i.e. when the order is handed to the
 * carrier, not when it is delivered.
 *
 * @param exportedOrders orders that were ACTUALLY exported - never the full requested set, or
 *                       skipped and failed orders get an invitation for a shipment that does
 *                       not exist
 * @param updateOrder    the `useOrders` mutator, passed in because it is a hook binding
 * @returns {Promise<{invited: number, flagged: number, failedShops: string[]}>}
 */
export const sendTrustpilotReviewInvitations = async (exportedOrders, updateOrder) => {
  const result = { invited: 0, flagged: 0, failedShops: [] };
  if (!exportedOrders || exportedOrders.length === 0) return result;

  const ordersByShop = _.groupBy(exportedOrders, (order) => order.shopSource || "VCE");

  for (const [shopSource, shopOrders] of Object.entries(ordersByShop)) {
    const invitations = buildTrustpilotInvitationList(shopOrders);

    // Nothing to send for this shop (all gmail, or all invited on an earlier run). Returning
    // here used to `return` out of the entire export handler and skip `refreshOrders()`.
    if (invitations.length === 0) continue;

    try {
      const idToken = await getFirebaseIDTokenForShop(shopSource);
      await axios.post(`${resolveShopApiUrl(shopSource)}${TRUSTPILOT_REVIEW_PATH}`, invitations, {
        headers: {
          Authorization: `${idToken}`,
        },
      });
      result.invited += invitations.length;
    } catch (err) {
      console.log(err);
      result.failedShops.push(shopSource);
    }

    // Known quirk, carried over unchanged on purpose: the flag is set on every exported order of
    // this shop - including the gmail addresses and the duplicates that were filtered out above -
    // and it is set even when the POST failed. Changing that would make a failed POST retry the
    // invitation on the next export run, which is better, but it is a customer-facing mail and
    // therefore a separate decision, not a side effect of moving this code.
    for (const order of shopOrders) {
      const success = await updateOrder(order, { trustpilotReviewInvitationSent: true });
      if (success !== false) result.flagged++;
    }
  }

  return result;
};
